import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import logo from "@/assets/rcn-logo.png";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAuthed, setIsAuthed] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthed(!!session);
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsAuthed(!!session);
    });
    return () => subscription.unsubscribe();
  }, []);

  const memberLink = isAuthed
    ? { href: "/portal", label: "Member Portal" }
    : { href: "/auth", label: "Member Login" };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          <a href="/" className="flex-shrink-0">
            <img src={logo} alt="Referral Connection Network" className="h-12 w-auto" width={367} height={120} />
          </a>

          {/* Tablet-only Tier 1: CTAs on the right */}
          <div className="hidden md:flex lg:hidden items-center gap-3">
            <Button variant="outline" asChild>
              <a href={memberLink.href}>{memberLink.label}</a>
            </Button>
            <Button asChild>
              <a href="/visit-a-meeting">Visit a Meeting</a>
            </Button>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-foreground hover:text-primary transition-colors">
                Chapters
                <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-popover border border-border">
                <DropdownMenuItem asChild>
                  <a href="/washington" className="cursor-pointer">
                    Washington, MO
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a href="/union" className="cursor-pointer">
                    Union, MO
                  </a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <a href="/impact" className="text-foreground hover:text-primary transition-colors">
              Our Impact
            </a>
            <a href="/benefits" className="text-foreground hover:text-primary transition-colors">
              Member Benefits
            </a>
            <Button variant="outline" asChild>
              <a href={memberLink.href}>{memberLink.label}</a>
            </Button>
            <Button asChild>
              <a href="/visit-a-meeting">
                Visit a Meeting
              </a>
            </Button>
          </div>

          {/* Mobile menu button */}
          <button onClick={() => setIsOpen(!isOpen)} className="md:hidden text-foreground hover:text-primary transition-colors">
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Tablet-only Tier 2: text links */}
        <div className="hidden md:flex lg:hidden items-center justify-center gap-10 py-3 border-t border-border bg-muted/40">
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1 text-foreground hover:text-primary transition-colors whitespace-nowrap">
              Chapters
              <ChevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-popover border border-border">
              <DropdownMenuItem asChild>
                <a href="/washington" className="cursor-pointer">Washington, MO</a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a href="/union" className="cursor-pointer">Union, MO</a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <a href="/impact" className="text-foreground hover:text-primary transition-colors whitespace-nowrap">
            Our Impact
          </a>
          <a href="/benefits" className="text-foreground hover:text-primary transition-colors whitespace-nowrap">
            Member Benefits
          </a>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden py-4 space-y-4 animate-fade-in">
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Chapters</p>
              <a href="/washington" className="block py-2 pl-4 text-foreground hover:text-primary transition-colors" onClick={() => setIsOpen(false)}>
                Washington, MO
              </a>
              <a href="/union" className="block py-2 pl-4 text-foreground hover:text-primary transition-colors" onClick={() => setIsOpen(false)}>
                Union, MO
              </a>
            </div>
            <a href="/impact" className="block py-2 text-foreground hover:text-primary transition-colors" onClick={() => setIsOpen(false)}>
              Our Impact
            </a>
            <a href="/benefits" className="block py-2 text-foreground hover:text-primary transition-colors" onClick={() => setIsOpen(false)}>
              Member Benefits
            </a>
            <Button variant="outline" className="w-full" asChild>
              <a href={memberLink.href}>{memberLink.label}</a>
            </Button>
            <Button className="w-full" asChild>
              <a href="/visit-a-meeting">
                Visit a Meeting
              </a>
            </Button>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;